import { useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ContentLayout } from '../../Layouts/ContentLayout/ContentLayout';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { WIRE_STATUS_DB } from '@/const/wire';
import { changeExecutorWireStatus, loadExecutorPaymentDetails, loadExecutorWire } from './api/api';
import { isCancelledWireStatus, isExecutedWireStatus } from '@/helpers/wireHelpers';
import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';
import { PageColumn } from '@/components/Page/PageColumn/PageColumn';
import { WireDetails } from './ui/WireDetails';
import { PageContent } from '@/components/Page/PageContent/PageContent';
import { Box } from '@/components/Box/Box';
import { getExecutorWiresPath } from '../path/path';
import { WaterMark } from '@/bundle/shared/components/WaterMark/WaterMark';
import { getIsAllowedToExecute } from './helpers/helpers';
import { Button } from '@/components/Button/Button';
import { UserDetailsWidget } from '@/bundle/shared/components/UserDetailsWidget/UserDetailsWidget';
import { PaymentDetailsDataWidget } from '@/bundle/shared/components/PaymentDetailsDataWidget/PaymentDetailsDataWidget';
import { showSnackbarResponseError } from '@/helpers/helpers';
import { useStatefulNavigation } from '@/hooks/useStatefulNavigation';

export const ExecutorWireDetailsPage = () => {
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const { navigateBack } = useStatefulNavigation();
  const [isOpenExecutedModal, setIsOpenExecutedModal] = useState<boolean>(false);

  const { data: executorWireData, isPending: isPendingExecutorWire } = useQuery({
    queryKey: ['load_executor_wire', id],
    queryFn: () => loadExecutorWire(id),
  });

  const { data: executorPaymentDetailsData, isFetching: isFetchingExecutorPaymentDetails } = useQuery({
    queryKey: ['load_executor_payment_details', id],
    queryFn: () => loadExecutorPaymentDetails(id),
    enabled: !!executorWireData?.body?.is_payment_details_exist,
  });

  const { mutate, isPending } = useMutation({
    mutationKey: ['execute_wire'],
    mutationFn: () => {
      return changeExecutorWireStatus(id, WIRE_STATUS_DB.EXECUTED);
    },
    onSuccess(response) {
      if (response.error) {
        return showSnackbarResponseError(response?.error);
      }

      setIsOpenExecutedModal(false);
      amplitudeService.logEvent(AMPLITUDE_EVENTS.ExecutorMarksAsExecutedSuccess);

      queryClient.setQueryData(['load_executor_wire', id], response);
    },
  });

  const executeWire = () => {
    setIsOpenExecutedModal(true);

    amplitudeService.logEvent(AMPLITUDE_EVENTS.ExecutorMarksAsExecutedRedirect);
  };

  const navigateToWires = () => {
    navigateBack(getExecutorWiresPath());
  };

  const executorWire = executorWireData?.body;
  const executorPaymentDetails = executorPaymentDetailsData?.body;

  const wireStatus = executorWire?.status;
  const isExecutedWire = isExecutedWireStatus(wireStatus);
  const isCancelledWire = isCancelledWireStatus(wireStatus);
  const showWaterMark = isExecutedWire || isCancelledWire;
  const isAllowedToExecute = getIsAllowedToExecute(wireStatus) && !isPendingExecutorWire;

  return (
    <ContentLayout
      title='Wire Details'
      breadcrumbs='Wires > Details'
      headerAction={
        isAllowedToExecute && (
          <Button width={160} mobileStretch onClick={executeWire}>
            Wire Executed
          </Button>
        )
      }
      backButton='Back to Wires'
      onBack={navigateToWires}
    >
      <PageBody>
        <PageContent>
          <PageColumn>
            {executorWire && (
              <>
                <Box position='relative'>
                  <WireDetails wire={executorWire} />
                  {showWaterMark && <WaterMark wireStatus={wireStatus} />}
                </Box>
                <UserDetailsWidget header='Recipient' user={executorWire?.assigned_user} />
                {executorWire?.assigned_executors?.map((executor, index) => {
                  const header = `Executor ${++index}`;

                  return <UserDetailsWidget key={executor?.id} header={header} user={executor?.user} />;
                })}
              </>
            )}
          </PageColumn>
          <PageColumn>
            <PaymentDetailsDataWidget
              paymentDetails={executorPaymentDetails}
              isLoading={isFetchingExecutorPaymentDetails}
              apiError={executorPaymentDetailsData?.error}
            />
          </PageColumn>
        </PageContent>
      </PageBody>

      <ConfirmModal
        isOpen={isOpenExecutedModal}
        header='Mark as Executed'
        isLoading={isPending}
        body='You are marking this wire as successfully executed. The recipient of this wire will be notified that the wire has been sent.'
        onClose={() => setIsOpenExecutedModal(false)}
        onConfirm={mutate}
      />
    </ContentLayout>
  );
};
